import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { CLAIM_TYPES, claimTypeStyle, derivarClaimType } from '../lib/claimType';
import { useI18n } from '../lib/i18n';

/**
 * EL CHIP DEL TIPO DE CLAIM, AL LADO DEL NOMBRE.
 *
 * Pedido de Manuel (2026-08-27): *"quiero que los clientes tengan un chip al lado de qué tipo de
 * claim es […] si no lo tiene que salga automático roof claim, a excepción de este que dice water
 * en el nombre […] y quiero que sea modificable, que cualquiera pueda modificarlo, pero que deje
 * el track de quién fue"*.
 *
 * SIEMPRE se ve algo: el tipo sale de `derivarClaimType` y ese nunca devuelve vacío. La diferencia
 * entre lo que alguien eligió y lo que dedujo la app se nota en el chip —el derivado va tenue y
 * con borde punteado— y se explica en el tooltip, que también dice quién lo puso y cuándo. Esa
 * distinción es la razón de que el derivado NO se escriba en la base: si se escribiera, corregir
 * el nombre del cliente dejaría el tipo viejo pegado y nadie sabría que fue la app quien lo puso.
 *
 * Se cambia acá mismo (select nativo invisible encima del chip, mismo patrón que los status del
 * drawer): un clic, elegir, listo. El guardado va por PUT /api/claims/:id/info, que ya valida
 * contra la lista cerrada, registra el diff en el activity log y sella `claim_type_by_user_id`.
 */
export function ClaimTypeChip({ claimId, claim, onSaved, size = 'md' }: {
  claimId: string;
  claim: { claim_type?: string | null; homeowner_name?: string | null; type_of_loss?: string | null; has_mitigation?: boolean | null; claim_type_by?: string | null; claim_type_at?: string | null };
  onSaved?: () => void;
  /** 'sm' para las filas de las listas; 'md' para los cabezales. */
  size?: 'sm' | 'md';
  /** Sin `onSaved` el chip igual guarda: el padre decide si recarga. */
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const d = derivarClaimType(claim);
  const st = claimTypeStyle(d.tipo);

  async function guardar(valor: string) {
    if (busy || !valor || valor === (claim.claim_type ?? '')) return;
    setBusy(true);
    try { await api.put(`/api/claims/${claimId}/info`, { claim_type: valor }); onSaved?.(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
    finally { setBusy(false); }
  }

  // Tooltip: qué dice el chip y de dónde salió. Si lo puso una persona, quién y cuándo.
  const origen = d.auto
    ? t(d.origen === 'name' ? 'cty.fromName' : d.origen === 'loss' ? 'cty.fromLoss' : d.origen === 'mitigation' ? 'cty.fromMitigation' : 'cty.fromDefault')
    : (claim.claim_type_by
      ? t('cty.setBy').replace('{who}', claim.claim_type_by).replace('{when}', claim.claim_type_at ? new Date(claim.claim_type_at).toLocaleDateString() : '—')
      : t('cty.setManually'));
  const title = `${t('cdw.claimType')}: ${d.tipo} — ${origen}\n${t('cty.clickToChange')}`;

  return (
    <span style={{ position: 'relative', display: 'inline-flex', opacity: busy ? 0.5 : 1 }} title={title}>
      <span
        className="badge"
        style={{
          fontSize: size === 'sm' ? 'var(--fs-2xs)' : 'var(--fs-xs)',
          fontWeight: 'var(--fw-bold)', letterSpacing: 0.3, textTransform: 'uppercase',
          padding: size === 'sm' ? '2px 8px' : '3px 10px',
          background: st.background, color: st.color,
          // Derivado = punteado y un punto menos de tinta: se lee igual, pero no se confunde con
          // un dato que alguien confirmó.
          border: d.auto ? '1px dashed currentColor' : '1px solid transparent',
          opacity: d.auto ? 0.82 : 1,
        }}
      >
        {t(`cty.${d.tipo.toLowerCase()}`)}
      </span>
      {/* Select invisible ENCIMA: click = desplegable nativo, sin un botón de editar aparte. */}
      <select
        value=""
        disabled={busy}
        onChange={(e) => void guardar(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        aria-label={t('cdw.claimType')}
        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%' }}
      >
        <option value="">{d.tipo}</option>
        {CLAIM_TYPES.map((o) => <option key={o} value={o}>{t(`cty.${o.toLowerCase()}`)}</option>)}
      </select>
    </span>
  );
}
